import React from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { TRANSPORT_DATE_INPUT_FORMATS, transportDatePickerProps } from './transportFormUtils';

dayjs.extend(customParseFormat);

/** A typed date in any accepted form („12.10.2026”, „2026-10-12”, „12/10/2026”); null = not a date. */
export function parseTypedTransportDate(text: string): Dayjs | null {
  const value = text.trim();
  if (!value) return null;
  for (const format of TRANSPORT_DATE_INPUT_FORMATS) {
    const parsed = dayjs(value, format, true);
    if (parsed.isValid()) return parsed;
  }
  return null;
}

type Props = Omit<DatePickerProps, 'value' | 'onChange'> & {
  value?: Dayjs | null;
  onChange?: (value: Dayjs | null) => void;
};

/**
 * Transport date field (round 2, U7): the antd picker commits a typed date
 * only on Enter; leaving the field with Tab or a click left „2026-10-12” in
 * the box and nothing in the form. This commits the typed text on blur too.
 */
const TransportDateInput: React.FC<Props> = ({ value, onChange, onBlur, ...rest }) => (
  <DatePicker
    {...transportDatePickerProps}
    {...rest}
    value={value ?? null}
    onChange={(next) => onChange?.((next as Dayjs | null) ?? null)}
    onBlur={(event) => {
      const typed = parseTypedTransportDate((event.target as HTMLInputElement).value || '');
      if (typed && (!value || !typed.isSame(value, 'day'))) onChange?.(typed);
      onBlur?.(event as never, {} as never);
    }}
  />
);

export default TransportDateInput;
